import type { ComponentPropsWithoutRef } from 'react';
import React from 'react';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { Props as AvatarProps } from 'src/components/ui/Avatar';
import Avatar from 'src/components/ui/Avatar';
import Typography from 'src/components/ui/Typography';

import { colours } from 'src/constants/colours';

import type { CompanyForActivity } from 'src/hooks/useActivityToDisplay';
import useActivityToDisplay, { useCompanyActivityToDisplay } from 'src/hooks/useActivityToDisplay';
import type {
    CompanyProfileQuery,
    PagesJaunesProQuery,
    Publisher,
    UserProfileQuery,
} from 'src/types/__generated__/graphql';
import { getPagesJaunesImageUrl } from 'src/utils/pagesJaunesUtil';
import getPublisherDetails from 'src/utils/meloUtils';

type UserProfile = Exclude<UserProfileQuery['userProfile'], null | undefined>;
type CompanyProfile = Exclude<CompanyProfileQuery['companyProfile'], null>;

const UserOrCompanyPreviewCore: React.FC<{
    company?: (CompanyForActivity & Pick<CompanyProfile, 'name' | 'image'>) | null;
    user?:
        | null
        | ({ showCompany: boolean; showActivity: boolean } & Pick<
              UserProfile,
              'fullName' | 'activity' | 'image' | 'affiliatedCompany'
          >);
    mainPublisher?: Publisher | null;
    pagesJaunesPro?: PagesJaunesProQuery['pagesJaunesProWithImagesUrls'];
    mainTypographyProps?: ComponentPropsWithoutRef<typeof Typography>;
    secondaryTypographyProps?: ComponentPropsWithoutRef<typeof Typography>;
    avatarProps?: ComponentPropsWithoutRef<typeof Avatar>;
    dataTestIdPrefix: string;
}> = ({
    user,
    company,
    mainPublisher,
    pagesJaunesPro,
    mainTypographyProps,
    secondaryTypographyProps,
    avatarProps,
    dataTestIdPrefix,
}) => {
    const { t } = useTranslation(['profile', 'common', 'company']);
    const getCompanyActivityToDisplay = useCompanyActivityToDisplay();
    const getActivityToDisplay = useActivityToDisplay();

    if (
        (user && company && pagesJaunesPro && mainPublisher) ||
        (!user && !company && !pagesJaunesPro && !mainPublisher)
    ) {
        return null;
    }

    const entity = user ?? company;
    const image = entity?.image?.url ?? pagesJaunesPro?.thumbnailPath;
    const avatarMedium = image ? { url: entity?.image?.url ?? getPagesJaunesImageUrl(image) } : undefined;

    let mainText: string | undefined | null;
    let secondaryText: string | undefined;
    let testKey;
    const avatarDynamicProps: Partial<AvatarProps> = {};
    if (company) {
        mainText = company.name;
        secondaryText = getCompanyActivityToDisplay(company);
        avatarDynamicProps.isCompany = true;
        testKey = 'company';
    } else if (pagesJaunesPro) {
        mainText = pagesJaunesPro.name;
        secondaryText = t(pagesJaunesPro.activityLabel ?? 'common:company');
        avatarDynamicProps.isCompany = true;
        testKey = 'pagesjaunespro';
    } else if (user) {
        mainText = user.fullName;
        const secondaryTextParts = [
            user.showActivity && getActivityToDisplay(user),
            user.showCompany && user.affiliatedCompany,
        ];
        secondaryText = secondaryTextParts.filter(Boolean).join(' · ');
        avatarDynamicProps.isCompany = false;
        avatarDynamicProps.size = 32;
        testKey = 'user';
    } else if (mainPublisher) {
        const { name, logo } = getPublisherDetails(mainPublisher);
        avatarDynamicProps.iconName = logo;
        mainText = name;

        secondaryText = t('common:externalWebsite');
        avatarDynamicProps.isCompany = true;
        avatarDynamicProps.isPublisher = true;
        testKey = 'mainPublisher';
    }

    return (
        <>
            <Avatar
                {...avatarDynamicProps}
                image={avatarMedium}
                data-target-id={`${dataTestIdPrefix}-${testKey}-logo`}
                badgeIcon={pagesJaunesPro ? 'PagesJaunes' : undefined}
                badgeColor="transparent"
                {...avatarProps}
            />
            <Box margin="0.15rem" marginLeft="0.35rem" overflow="hidden" flex="1">
                <Typography
                    data-target-id={`${dataTestIdPrefix}-${testKey}-name`}
                    variant="h5"
                    maxWidth="100%"
                    color={colours.primary.main}
                    noWrap
                    {...mainTypographyProps}
                >
                    {mainText}
                </Typography>
                <Typography
                    data-target-id={`${dataTestIdPrefix}-${testKey}-label`}
                    variant="subtitle2"
                    color={undefined}
                    noWrap
                    {...secondaryTypographyProps}
                >
                    {secondaryText}
                </Typography>
            </Box>
        </>
    );
};

export default UserOrCompanyPreviewCore;
